<template>
  <div id="activity-audit-reasons-for-change" class="reasons-for-change-block">
    <el-row class="topMargin">
      <v-client-table class="hiaTable" v-loading="loading" element-loading-spinner="atom-audit-loader" ref="reasonsGrid" id="reasonsGrid" :data="reasonsForSelectedType" :columns="reasonColumns" :options="reasonOptions" uniqueKey="reasonForChangeId">
        <div slot="filter__isInactive">
          <input type="checkbox" @change="$refs.reasonsGrid.setFilter({ isInactive: $event.target.checked })" />
        </div>
        <template slot="description" slot-scope="props">
          <div class="el-input--mini">
            <input class="el-input__inner" type="text" v-model="props.row.description" @change="updateReason(props.row)" />
          </div>
        </template>
        <template slot="isInactive" slot-scope="props">
          <input :key="props.row.reasonForChangeId" type="checkbox" v-model="props.row.isInactive" @change="updateReason(props.row)" />
        </template>
        <template slot="delete" slot-scope="props">
          <div style="text-align: center;">
            <i class="el-icon-close delete-button" @click="deleteReason(props.row)"></i>
          </div>
        </template>
      </v-client-table>
    </el-row>

    <el-form label-position="left" label-width="150px" size="mini" style="margin-top: 10px;">
      <el-popover placement="right" ref="newReasonPopover" :title="`Add Reason for Change to ${selectedType.name}`" width="360" trigger="click" @show="resetAddReasonAutocomplete">
        <el-form :model="addReasonForm" ref="addReasonForm" size="mini" :rules="addReasonForm.rules" label-position="top" label-width="180px" @submit.native.prevent>
          <el-form-item prop="description" label="Reason for Change">
            <el-autocomplete class="inline-input audit-rfc-autocomplete" v-model="addReasonForm.description" size="mini" :fetch-suggestions="querySearchReasons" placeholder="Type or select an existing reason" :trigger-on-focus="true" @select="onSelectExistingReason" @input="onAddReasonInput"></el-autocomplete>
          </el-form-item>
          <el-form-item>
            <el-button-group>
              <el-button class="hiaButton" plain round size="mini" type="text" @click="closeNewReasonPopover()">Cancel</el-button>
              <el-button class="hiaButton" plain round size="mini" @click="addReason('addReasonForm')">Add</el-button>
            </el-button-group>
          </el-form-item>
        </el-form>

        <el-button slot="reference" class="hiaButton" plain round size="mini" icon="el-icon-plus">Add Reason for Change</el-button>
      </el-popover>
    </el-form>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import hiaTable from '@/mixins/hiaTable'

  export default {
    name: 'ActivityAuditReasonsForChange',
    mixins: [hiaTable, HiaAuthorization],
    props: ['selectedType'],
    data() {
      return {
        loading: false,
        reasonColumns: ['description', 'isInactive', 'delete'],
        reasonOptions: {
          filterByColumn: false,
          filterable: false,
          sortable: ['description'],
          orderBy: {
            column: 'description',
            ascending: true
          },
          headings: {
            isInactive: 'Inactive',
            delete: function(h) {
              return ''
            },
            description: 'Reason for Change'
          },
          perPage: 10,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
        },
        addReasonForm: {
          description: '',
          existingReasonId: null,
          rules: {
            description: [{ required: true, message: 'Please enter a reason', trigger: 'none' }]
          }
        }
      }
    },
    methods: {
      ...mapActions('managedLists/activityAuditReasonsForChange/', ['POST_ACTIVITY_AUDIT_REASON_FOR_CHANGE', 'DELETE_ACTIVITY_AUDIT_REASON_FOR_CHANGE', 'PUT_ACTIVITY_AUDIT_REASON_FOR_CHANGE']),
      querySearchReasons(queryString, cb) {
        const q = (queryString || '').trim().toLowerCase()
        const links = this.allReasonAutocompleteItems
        const results = q ? links.filter(item => item.value.toLowerCase().indexOf(q) !== -1) : links
        cb(results)
      },
      onSelectExistingReason(item) {
        this.addReasonForm.existingReasonId = item.reasonForChangeId
        this.addReasonForm.description = item.value
      },
      onAddReasonInput() {
        this.addReasonForm.existingReasonId = null
      },
      resetAddReasonAutocomplete() {
        this.addReasonForm.existingReasonId = null
      },
      findExactReasonIdByDescription(text) {
        const t = (text || '').trim().toLowerCase()
        if (!t) return null
        const r = (this.activityAuditReasonForChanges || []).find(x => (x.description || '').trim().toLowerCase() === t)
        return r ? r.reasonForChangeId : null
      },
      addReason(refName) {
        this.$refs[refName].validate(async valid => {
          if (valid) {
            try {
              this.loading = true
              const typeId = this.selectedType.activityAuditTypeId
              const trimmed = (this.addReasonForm.description || '').trim()
              let linkExistingId = this.addReasonForm.existingReasonId
              if (linkExistingId == null) {
                linkExistingId = this.findExactReasonIdByDescription(trimmed)
              }
              let payload
              if (linkExistingId != null) {
                payload = {
                  reasonForChange: {
                    existingReasonForChangeId: linkExistingId,
                    activityAuditTypeId: typeId
                  }
                }
              } else {
                payload = {
                  reasonForChange: {
                    activityAuditTypeId: typeId,
                    description: trimmed,
                    isInactive: false
                  }
                }
              }
              await this.POST_ACTIVITY_AUDIT_REASON_FOR_CHANGE(payload)
              this.$refs[refName].resetFields()
              this.addReasonForm.existingReasonId = null
              this.closeNewReasonPopover()
              this.loading = false
              this.$message({
                message: linkExistingId != null ? 'Reason for Change linked to this activity audit type' : 'Reason for Change added',
                type: 'success'
              })
            } catch (err) {
              this.loading = false
              this.$message({
                message: `Error adding reason: ${err.message}`,
                type: 'error'
              })
            }
          }
        })
      },
      async updateReason(row) {
        const index = this.activityAuditReasonForChanges.findIndex(x => x.reasonForChangeId === row.reasonForChangeId)
        this.$set(this.activityAuditReasonForChanges, index, row)

        try {
          this.loading = true
          await this.PUT_ACTIVITY_AUDIT_REASON_FOR_CHANGE(row)
          this.loading = false
          this.$message({
            message: 'Reason for Change saved',
            type: 'success'
          })
        } catch (err) {
          this.loading = false
          this.$message({
            message: 'Error saving reason',
            type: 'error'
          })
        }
      },
      async deleteReason(row) {
        await this.$confirm('Permanently delete this reason for change? If it has been used on records it will be marked inactive instead.', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        try {
          this.loading = true
          await this.DELETE_ACTIVITY_AUDIT_REASON_FOR_CHANGE(row)
          this.loading = false
          this.$message({
            message: 'Reason for Change removed',
            type: 'success'
          })
        } catch (err) {
          this.loading = false
          this.$message({
            message: `Error deleting reason: ${err.message}`,
            type: 'error'
          })
        }
      },
      closeNewReasonPopover() {
        const popper = this.$refs.newReasonPopover
        if (popper) {
          this.addReasonForm.description = ''
          this.addReasonForm.existingReasonId = null
          popper.doDestroy()
          popper.doClose()
        }
      }
    },
    computed: {
      ...mapState('managedLists/activityAuditReasonsForChange/', ['activityAuditReasonForChanges']),
      allReasonAutocompleteItems() {
        return (this.activityAuditReasonForChanges || []).map(r => ({
          value: r.description || '',
          reasonForChangeId: r.reasonForChangeId
        }))
      },
      reasonsForSelectedType() {
        const typeId = this.selectedType && this.selectedType.activityAuditTypeId
        if (typeId == null) return []
        const reasons = this.activityAuditReasonForChanges || []
        return reasons.filter(r => {
          const ids = r.activityAuditTypeIds || []
          return ids.includes(typeId)
        })
      }
    }
  }
</script>

<style scoped>
  .reasons-for-change-block {
    margin-top: 16px;
  }

  .topMargin {
    margin: 10px 0px 0px 0px;
  }

  >>> .VuePagination__count {
    display: none;
  }

  .audit-rfc-autocomplete {
    width: 100%;
  }
</style>
